"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Layers,
  Sparkles,
  Stethoscope,
  Crown,
  Brain,
  Mic,
  ArrowRight,
  Heart,
  Compass,
  Activity,
  Briefcase,
  Building2,
  Target,
  Users,
  ShieldCheck,
  TrendingUp,
  Clock,
  Calendar,
  PlayCircle,
  Award,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { BentoBlock } from "@/lib/blocks/bento-block";
import { FeatureGridBlock } from "@/lib/blocks/feature-grid-block";
import { AccordionBlock } from "@/lib/blocks/accordion-block";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
};

const TOPICS = [
  {
    num: "01",
    title: "Operational Excellence Systems",
    body: "Operational excellence is not a culture — it is a system. The workshop teaches teams to design and implement operating systems that drive execution with the clinical precision of sovereign-scale operations. The diagnostic phase maps the team&apos;s current operational gaps. The prescription phase designs the specific interventions. Teams leave with a complete operational excellence system designed for their specific context.",
    icon: Layers,
    accent: "red",
  },
  {
    num: "02",
    title: "The Clinical Method for Teams",
    body: "The physician&apos;s method translated into team practices. Teams learn to diagnose organizational challenges with clinical precision, triage competing demands with emergency medicine urgency, execute with protocol-driven discipline, and measure outcomes with clinical specificity. Hands-on exercises apply the clinical method to actual operational challenges — producing both a team that understands the method and a team that has applied it to real problems.",
    icon: Stethoscope,
    accent: "purple",
  },
  {
    num: "03",
    title: "Building High-Performance Diverse Teams",
    body: "A decade of operational data proving that teams composed of over 70% women produce the most reliable outcomes. The 70% Operating Model moves beyond theory into implementation — recruitment strategies, integration protocols, performance measurement systems, and accountability structures. Teams design their own diversity operating models grounded in verified data.",
    icon: Heart,
    accent: "emerald",
  },
  {
    num: "04",
    title: "Evidence-Based Decision Making",
    body: "Replacing intuition with operational rigor. Teams learn to apply the clinical method to organizational decision-making — using data, protocols, and evidence to drive decisions rather than relying on experience, instinct, or hierarchy. The workshop addresses cognitive biases that undermine organizational decision-making and provides structural countermeasures grounded in the diagnostic discipline of medicine.",
    icon: Brain,
    accent: "cyan",
  },
  {
    num: "05",
    title: "Crisis-Ready Operations",
    body: "Building teams that withstand disruption. The clinical method for crisis response — how to triage under pressure, maintain operational continuity when systems fail, and build resilience through protocol-driven preparedness. Teams learn the crisis response protocols that have sustained sovereign-scale operations, applied to their own organizational context.",
    icon: ShieldCheck,
    accent: "amber-gold",
  },
];

const FORMATS = [
  { icon: Clock, title: "Half-Day Intensive", body: "3–4 hours of concentrated operational training. Designed for focused, high-impact workshops that produce immediate implementation. Suitable for executive retreats and team offsites.", accent: "red" },
  { icon: Calendar, title: "Full-Day Program", body: "6–8 hours of comprehensive workshop engagement. Deeper exploration of the operating method with hands-on application. Suitable for leadership team development.", accent: "purple" },
  { icon: Layers, title: "Multi-Day Immersion", body: "Extended workshop programs spanning multiple days. The deepest form of team training — combining lecture, application, and implementation planning. Suitable for organizational transformation.", accent: "cyan" },
  { icon: PlayCircle, title: "Virtual & Remote", body: "Full operational standards maintained through digital delivery. Interactive exercises, breakout sessions, and implementation planning — optimized for distributed teams and global organizations.", accent: "emerald" },
  { icon: TrendingUp, title: "Workshop + Follow-Up", body: "Workshop engagement paired with structured follow-up sessions. The workshop introduces the operating method; follow-up sessions embed it within the team&apos;s daily practice. Sustained implementation.", accent: "amber-gold" },
  { icon: Target, title: "Custom Programs", body: "Tailored to the organization&apos;s specific operational challenges, team composition, and strategic objectives. The workshop is calibrated — not genericized — to address the specific realities of the team and the organization.", accent: "denim" },
];

const AUDIENCES = [
  { icon: Crown, title: "Corporate Leadership Teams", body: "C-suite and senior leadership teams facing execution challenges, organizational transformation, or operational complexity requiring coordinated response.", accent: "red" },
  { icon: Building2, title: "Government Departments", body: "Government bodies and departments requiring operational discipline, governance transformation, or strategic capability development at scale.", accent: "purple" },
  { icon: Briefcase, title: "Organizational Development", body: "OD programs and HR leadership seeking to translate the operating method into team capability, leadership development, and organizational performance.", accent: "cyan" },
  { icon: Users, title: "Team Offsites & Retreats", body: "Executive retreats and team offsites designed to produce lasting operational change — not just shared experiences but embedded operating mechanisms.", accent: "emerald" },
  { icon: Target, title: "Cross-Functional Teams", body: "Cross-functional teams facing operational challenges that require coordinated response and protocol-driven execution across organizational boundaries.", accent: "amber-gold" },
  { icon: Activity, title: "Transformation Programs", body: "Organizations in the midst of operational transformation requiring intensive team training to embed the operating method within their change management process.", accent: "denim" },
];

const PRINCIPLES = [
  { num: "01", title: "Diagnose Before Prescribing", body: "Every workshop begins with understanding the team&apos;s current operational landscape — the specific challenges, the existing capabilities, the gaps, the root causes. No workshop is designed without diagnosis.", accent: "red" },
  { num: "02", title: "Protocols Over Aspirations", body: "The deliverable is not inspiration. It is infrastructure. Teams leave with protocols they can follow, checklists they can deploy, decision frameworks they can apply, and accountability structures they can enforce.", accent: "purple" },
  { num: "03", title: "Outcome Accountability", body: "Every workshop is measured by what teams can do differently after the workshop, not by how participants felt during it. Post-workshop assessments verify implementation: protocols established, checklists active, frameworks deployed.", accent: "cyan" },
  { num: "04", title: "Evidence Over Theory", body: "Every framework taught has been tested across sovereign-scale deployments. The protocols are extracted from the operating method that drives sovereign-scale operations — not borrowed from management textbooks.", accent: "emerald" },
  { num: "05", title: "Systems Over Slides", body: "The workshop delivers operating systems — the kind that persist beyond the workshop itself, that teams follow daily, that produce consistent, measurable outcomes under pressure. Not slide decks. Infrastructure.", accent: "amber-gold" },
  { num: "06", title: "Discretion as Foundation", body: "National security level confidentiality, binding agreements, compartmentalized operations. What is discussed in the workshop stays in the workshop. Zero information leakage.", accent: "denim" },
];

export default function CorporateWorkshopsPage() {
  return (
    <>
      <PageHero
        icon={Layers}
        overline="Service · Corporate Workshops"
        overlineClassName="text-lime"
        title={
          <>
            Team Training That Installs <span className="text-gradient-premium">Operating Systems.</span>
          </>
        }
        subtitle="Intensive team training programs that translate the clinical discipline into practical, implementable team practices. Not presentations. Operational immersions — teams leave with protocols established, checklists implemented, decision frameworks deployed, and outcome accountability embedded."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Services", href: "/services/" }, { label: "Corporate Workshops" }]}
        badge="Operating systems, not slide decks"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.lime.pill} mb-5`}>
              <Sparkles className="size-3.5 text-lime" />
              <span className="text-overline text-lime">The Thesis</span>
            </div>
            <h2 className="text-display-md mb-5">
              Workshops that embed the operating method within teams.
            </h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The corporate workshops do not merely inform teams — they transform how teams operate. Grounded in the clinical discipline of a physician and forged through fifteen-plus years of sovereign-scale operations, these workshops translate the operating method into practical, implementable team practices. Every workshop produces protocols established, checklists implemented, decision frameworks deployed, and outcome accountability embedded.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The Triple Threat — the clinical method of a physician, the systems architecture of a technology operator, and the strategic discipline of a political operator — is brought to every workshop. No other workshop facilitator at this level operates at this intersection with verified, sovereign-scale results.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              Organizations that engage the workshops are not purchasing training. They are installing an operating system within their teams. The workshops represent the most direct path to embedding the operating method within an organization&apos;s teams, producing measurable improvement in decision speed, execution reliability, and organizational resilience.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {PRINCIPLES.map((p, i) => {
              const a = ACCENT[p.accent];
              return (
                <motion.div
                  key={p.num}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                      <span className="text-caption font-bold text-white">{p.num}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className={`text-heading-sm mb-1.5 ${a.text}`}>{p.title}</h3>
                      <p className="text-caption text-muted-foreground leading-relaxed">{p.body}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <Layers className="size-3.5 text-red" />
            <span className="text-overline text-red">The Five Workshop Topics</span>
          </div>
          <h2 className="text-display-md mb-4">Topics that produce measurable operational improvement.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Each topic addresses a distinct dimension of operational leadership. All drawn from verified sovereign-scale experience, all producing implementable infrastructure, all measuring success by what teams can do differently afterward.
          </p>
        </div>
        <div className="space-y-4">
          {TOPICS.map((t, i) => {
            const a = ACCENT[t.accent];
            return (
              <motion.div
                key={t.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-6 lg:p-7 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="grid lg:grid-cols-[auto_1fr] gap-6">
                  <div className={`size-14 rounded-2xl bg-gradient-sovereign flex items-center justify-center shadow-xl ring-2 ${a.ring} group-hover:scale-110 group-hover:rotate-3 transition-all`}>
                    <t.icon className="size-7 text-white" />
                  </div>
                  <div>
                    <div className="flex items-baseline gap-3 mb-2">
                      <span className={`text-overline ${a.text} opacity-70`}>Topic {t.num}</span>
                    </div>
                    <h3 className={`text-display-sm mb-3 ${a.text}`}>{t.title}</h3>
                    <p className="text-body-md text-muted-foreground leading-relaxed">{t.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <Calendar className="size-3.5 text-purple" />
            <span className="text-overline text-purple">Workshop Formats</span>
          </div>
          <h2 className="text-display-md mb-4">Flexibility for every organizational requirement.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Workshop formats range from half-day intensives to multi-day programs, tailored to the organization&apos;s specific operational challenges, team composition, and strategic objectives. Virtual and remote delivery available.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {FORMATS.map((f, i) => {
            const a = ACCENT[f.accent];
            return (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className={`size-11 rounded-xl bg-gradient-sovereign flex items-center justify-center mb-3 shadow-lg`}>
                  <f.icon className="size-5 text-white" />
                </div>
                <h3 className={`text-heading-md mb-2 ${a.text}`}>{f.title}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{f.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Users className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">Ideal Clients</span>
          </div>
          <h2 className="text-display-md mb-4">Who books the corporate workshops.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Leadership teams, government departments, organizational development programs, team offsites, and cross-functional groups whose execution challenges demand the same rigor that governs sovereign-scale operations.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {AUDIENCES.map((a, i) => {
            const ac = ACCENT[a.accent];
            return (
              <motion.div
                key={a.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${ac.bg} to-transparent border ${ac.border} hover:scale-[1.01] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <a.icon className="size-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-md mb-1.5 ${ac.text}`}>{a.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{a.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.lime.pill} mb-4`}>
            <Layers className="size-3.5 text-lime" />
            <span className="text-overline text-lime">Workshop Format Bento</span>
          </div>
          <h2 className="text-display-md mb-4">Workshop sessions, structures, and outputs.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Each workshop is composed of sessions that produce specific operational outputs. The bento format maps the workshop's component pieces — the structure that produces operating systems, not slide decks.
          </p>
        </div>
        <BentoBlock
          items={[
            { title: "Diagnostic Briefing", body: "Opening session mapping the team's current operational landscape. The diagnostic identifies specific challenges, existing capabilities, gaps, and root causes. Every workshop begins with diagnosis — the same discipline that governs sovereign-level engagements.", icon: <Stethoscope className="size-5" />, accent: "red", span: "md" },
            { title: "Protocol Design Lab", body: "Hands-on session where teams design the specific protocols they will deploy. The lab produces written, implementable protocols for recurring operational challenges — the mechanisms that replace ad hoc decision-making.", icon: <Layers className="size-5" />, accent: "purple", span: "sm" },
            { title: "Checklist Construction", body: "Critical step construction for the highest-stakes operations. Teams build the checklists that prevent failure under pressure — the operational insurance that has produced zero security incidents across sovereign-scale deployments.", icon: <ShieldCheck className="size-5" />, accent: "cyan", span: "sm" },
            { title: "Clinical Method Application", body: "Intensive session applying the physician's method to actual team challenges. The session produces both a team that understands the method and a team that has applied it to real problems — not theoretical exercise.", icon: <Stethoscope className="size-5" />, accent: "emerald", span: "lg" },
            { title: "Decision Framework Workshop", body: "Collaborative construction of evidence-based decision frameworks. Teams replace intuition with methodology, instinct with protocol, hierarchy with evidence — the decision-making infrastructure that produces consistent outcomes.", icon: <Brain className="size-5" />, accent: "amber-gold", span: "sm" },
            { title: "Accountability Architecture", body: "Design session for the structural mechanisms that sustain execution over time. Teams build the accountability structures that prevent strategy-to-execution gaps from widening under pressure.", icon: <Target className="size-5" />, accent: "denim", span: "sm" },
            { title: "Implementation Planning", body: "Closing session producing the implementation roadmap. Each team leaves with a written plan specifying what protocols to deploy first, who owns each, what metrics will measure success, and the timeline for full adoption.", icon: <Activity className="size-5" />, accent: "lime", span: "md" },
            { title: "70% Composition Design", body: "Specialized session for teams building diverse, high-performance teams. Teams learn the operational case for majority-female teams and design their own diversity operating models grounded in verified data — not ideology.", icon: <Heart className="size-5" />, accent: "emerald", span: "sm" },
          ]}
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <Award className="size-3.5 text-purple" />
            <span className="text-overline text-purple">Workshop Capabilities</span>
          </div>
          <h2 className="text-display-md mb-4">What teams are capable of after a workshop.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The workshop produces measurable capability. Each capability is assessed before and after the engagement — the difference is the workshop's verified output.
          </p>
        </div>
        <FeatureGridBlock
          features={[
            { title: "Protocol Drafting Capability", body: "Teams draft, refine, and operationalize protocols for their specific recurring challenges — the documentation that ensures consistency beyond the workshop itself.", accent: "red" },
            { title: "Clinical Assessment Skill", body: "Diagnostic precision — the ability to assess organizational challenges with forensic clarity, identifying root causes rather than symptoms.", accent: "purple" },
            { title: "Triage Under Pressure", body: "The discipline of prioritizing when multiple demands compete — emergency medicine's triage framework, applied to leadership under load.", accent: "cyan" },
            { title: "Evidence-Based Decision Making", body: "Replacing intuition with operational rigor — data, protocols, and evidence driving decisions rather than experience, instinct, or hierarchy.", accent: "emerald" },
            { title: "Accountability Enforcement", body: "The structural mechanisms that ensure plans translate into action and action produces results. Accountability that persists beyond the workshop itself.", accent: "amber-gold" },
            { title: "Cross-Functional Protocol Use", body: "Coordinated response across organizational boundaries — protocol-driven execution that operates the same way regardless of which team is engaged.", accent: "denim" },
          ]}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <Sparkles className="size-3.5 text-red" />
            <span className="text-overline text-red">Frequently Asked</span>
          </div>
          <h2 className="text-display-md mb-4">Questions organizations ask before booking.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Workshop logistics, customization, and post-workshop follow-up are common topics. The diagnostic conversation covers everything the FAQ cannot.
          </p>
        </div>
        <AccordionBlock
          items={[
            { q: "How is a workshop customized to our team?", a: "Every workshop begins with a diagnostic phase — understanding the team's current operational landscape, the specific challenges, the existing capabilities, the gaps, the root causes. The workshop is then calibrated, not genericized, to address the specific realities of the team and the organization." },
            { q: "What is the minimum team size for a workshop?", a: "Workshops are effective for teams as small as six and as large as fifty. For larger groups, the workshop can be delivered as a series — foundational sessions followed by team-specific breakouts. The format is adapted to the team's size and composition." },
            { q: "Do participants receive materials after the workshop?", a: "Yes. Every workshop includes a digital resource package containing the frameworks, protocols, and decision frameworks developed during the session. Implementation guides, reference materials, and follow-up exercises are distributed to all participants and the organization's leadership." },
            { q: "Is follow-up support available after the workshop?", a: "Yes. Follow-up sessions are available — typically 60 to 90 minutes each — allowing leadership teams to ask questions, apply the frameworks to specific challenges, and receive guidance on implementation. The follow-up bridges the gap between workshop awareness and organizational adoption." },
            { q: "Can the workshop be combined with advisory or consulting?", a: "Yes. Workshops frequently combine with executive advisory for ongoing leadership development, executive mentorship for individual leader development, and operational consulting for organization-wide transformation. The combinations are designed around the diagnostic data and the outcomes the organization seeks." },
            { q: "What is the cost of a corporate workshop?", a: "Workshop fees are discussed during the diagnostic phase, tailored to the format, duration, audience size, customization requirements, and follow-up scope. The diagnostic conversation determines scope and fee structure simultaneously. Every workshop is customized — there are no standard packages." },
            { q: "How is workshop effectiveness measured?", a: "Effectiveness is measured through pre-workshop and post-workshop capability assessments, implementation verification, and follow-up progress reviews. The measurement framework mirrors the accountability standard that governs medical practice: the team either can do the work differently or cannot." },
            { q: "How far in advance should workshops be booked?", a: "Early booking is recommended. The preparation phase typically requires three to four weeks for optimal customization, though shorter timelines can be accommodated. Workshop engagements are scheduled based on the organization's calendar and the team's availability." },
          ]}
        />
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-lime/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-emerald/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Layers className="size-10 text-lime mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Book a corporate workshop.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Every workshop begins with a diagnostic phase — understanding the team&apos;s current operational landscape, the existing capabilities, the gaps. The workshop is then calibrated to address the specific realities of the team and the organization.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Book Workshop
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/services/keynote-speaking/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Brain className="size-4" />
                Keynote Speaking
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Keynote Speaking", description: "Operational authority delivered from the stage. Six signature topics.", href: "/services/keynote-speaking/", icon: Mic, accent: "red" },
          { title: "Executive Advisory", description: "The operating method embedded in leadership teams and daily practice.", href: "/services/executive-advisory/", icon: Briefcase, accent: "purple" },
          { title: "Operational Consulting", description: "Sovereign-scale operational transformation across organizations.", href: "/services/operational-consulting/", icon: Activity, accent: "amber-gold" },
          { title: "All Services", description: "Ten engagement types across six service modalities. One operating method.", href: "/services/", icon: Compass, accent: "lime" },
        ]}
      />
    </>
  );
}
